import { AsyncPipe, NgTemplateOutlet } from '@angular/common'
import { ChangeDetectionStrategy, Component, inject, input, Input, output } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { MatIconButton } from '@angular/material/button'
import { MatIcon } from '@angular/material/icon'
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu'
import { MatTab, MatTabGroup } from '@angular/material/tabs'
import { MatToolbar } from '@angular/material/toolbar'
import { ActivatedRoute } from '@angular/router'
import {
  booleanTransformFn,
  ElderBasicPaneLayoutComponent,
  ElderHeaderComponent,
  ElderPaneActionsComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
  ElderPaneHeaderComponent,
  ElderPaneTitleComponent,
  ElderTabDirective,
  ElderTabGroupRoutingDirective,
  ElderThemeDirective,
} from '@elderbyte/ngx-starter'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { Observable } from 'rxjs'
import { newPackageType, PackageType } from '../package-type'
import { PackageTypeDetailController } from './package-type-detail-controller.service'
import { PackageTypeGeneralComponent } from './package-type-general/package-type-general.component'
import { PackageTypeLabelingComponent } from './package-type-labeling/package-type-labeling.component'
import { PackageTypePresetsComponent } from './package-type-presets/package-type-presets.component'

@Component({
  selector: 'app-package-type-detail',
  templateUrl: './package-type-detail.component.html',
  styleUrls: ['./package-type-detail.component.scss'],
  providers: [PackageTypeDetailController],
  imports: [
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
    ElderPaneHeaderComponent,
    ElderPaneActionsComponent,
    ElderPaneContentComponent,
    ElderPaneTitleComponent,
    MatToolbar,
    ElderThemeDirective,
    MatIconButton,
    MatIcon,
    NgTemplateOutlet,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    FormsModule,
    ElderHeaderComponent,
    AsyncPipe,
    ElderTranslatePipe,
    ElderTabGroupRoutingDirective,
    MatTabGroup,
    MatTab,
    ElderTabDirective,
    PackageTypeLabelingComponent,
    PackageTypePresetsComponent,
    PackageTypeGeneralComponent,
  ],
})
export class PackageTypeDetailComponent {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private readonly controller = inject(PackageTypeDetailController)

  public readonly embedded = input(false, { transform: booleanTransformFn })

  public readonly close = output<Event>()

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const route = inject(ActivatedRoute)
    const routeType = route.snapshot.data['type']
    this.packageType = routeType ?? newPackageType()
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  public get packageType$(): Observable<PackageType> {
    return this.controller.packageType$
  }

  public get packageType(): PackageType {
    return this.controller.packageType
  }

  @Input()
  public set packageType(type: PackageType | undefined) {
    if (type) {
      this.controller.packageType = type
    }
  }

  public get creationMode$(): Observable<boolean> {
    return this.controller.creationMode$
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public get canDelete(): boolean {
    return false
  }

  public onSave(event: Event): void {
    this.controller.save()
  }

  public onDelete(event: Event): void {
    this.controller.delete()
  }

  public onClose(event: Event): void {
    this.emitClose(event)
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private emitClose(event: Event): void {
    this.close.emit(event)
  }
}
